import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSitesListbyclientId } from "../store/actions";

interface BreadcrumbItem {
  label: string;
  path: string | null;
}

const useBreadcrumbs = (): BreadcrumbItem[] => {
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const pathname = location.pathname;

  const sitelist = useSelector((state: any) => state?.getsiteData?.sitelist) || [];
  const userRoleId = typeof window !== "undefined" ? localStorage.getItem("userRoleId") : null;
  const clientId = typeof window !== "undefined" ? localStorage.getItem("clientId") : null;

  // Extract siteId from URL for client routes or admin routes
  const siteId = params.siteId || params.id;

  // Load site list if we need site name but don't have it yet
  useEffect(() => {
    if (siteId && sitelist.length === 0 && clientId) {
      dispatch(
        getSitesListbyclientId({
          clientId,
          pageNumber: "1",
          pageSize: "1000",
          sortColumn: "",
          sortDirection: "",
          searchKeyword: "",
        })
      );
    }
  }, [siteId, sitelist.length, clientId, dispatch]);

  // Resolve site name from sitelist
  const resolveSiteName = (id: string | undefined): string => {
    if (!id) return "";
    const site = sitelist.find((s: any) => String(s.siteId) === String(id));
    return site?.siteName || "";
  };

  // Get category name from location state
  const category = (location.state as any)?.category || "";

  // Build breadcrumbs based on route
  const crumbs: BreadcrumbItem[] = [];

  // Client routes (userRoleId '2')
  if (userRoleId === "2") {
    // /client - home, no breadcrumbs
    if (pathname === "/client") {
      return [];
    }

    crumbs.push({ label: "Home", path: "/client" });

    // /dashboard/:siteId
    const dashboardMatch = pathname.match(/^\/dashboard\/(\d+)$/);
    if (dashboardMatch) {
      const siteName = resolveSiteName(dashboardMatch[1]);
      if (siteName) crumbs.push({ label: siteName, path: null });
      return crumbs;
    }

    // /manuals/:siteId
    const manualsMatch = pathname.match(/^\/manuals\/(\d+)$/);
    if (manualsMatch) {
      const siteName = resolveSiteName(manualsMatch[1]);
      if (siteName) crumbs.push({ label: siteName, path: `/dashboard/${manualsMatch[1]}` });
      crumbs.push({ label: "O&M Manuals", path: null });
      return crumbs;
    }

    // /drawings/:siteId
    const drawingsMatch = pathname.match(/^\/drawings\/(\d+)$/);
    if (drawingsMatch) {
      const siteName = resolveSiteName(drawingsMatch[1]);
      if (siteName) crumbs.push({ label: siteName, path: `/dashboard/${drawingsMatch[1]}` });
      crumbs.push({ label: "Drawings", path: null });
      return crumbs;
    }

    // /changerequests/:siteId
    const changeRequestsMatch = pathname.match(/^\/changerequests\/(\d+)$/);
    if (changeRequestsMatch) {
      const siteName = resolveSiteName(changeRequestsMatch[1]);
      if (siteName) crumbs.push({ label: siteName, path: `/dashboard/${changeRequestsMatch[1]}` });
      crumbs.push({ label: "Change Requests", path: null });
      return crumbs;
    }

    // /drawing-detail/:id/:siteId
    const drawingDetailMatch = pathname.match(/^\/drawing-detail\/(\d+)\/(\d+)$/);
    if (drawingDetailMatch) {
      const detailSiteId = drawingDetailMatch[2];
      const siteName = resolveSiteName(detailSiteId);
      if (siteName) crumbs.push({ label: siteName, path: `/dashboard/${detailSiteId}` });
      crumbs.push({ label: "Drawings", path: `/drawings/${detailSiteId}` });
      if (category) crumbs.push({ label: category, path: null });
      return crumbs;
    }
  }

  // Admin routes (userRoleId '1')
  if (userRoleId === "1") {
    crumbs.push({ label: "Home", path: "/manageuser" });

    // /manageuser
    if (pathname === "/manageuser") {
      crumbs.push({ label: "Users", path: null });
      return crumbs;
    }

    // /manageclients
    if (pathname === "/manageclients") {
      crumbs.push({ label: "Clients", path: null });
      return crumbs;
    }

    // /managesites/manual/:siteId
    const adminManualMatch = pathname.match(/^\/managesites\/manual\/(\d+)$/);
    if (adminManualMatch) {
      crumbs.push({ label: "Clients", path: "/manageclients" });
      crumbs.push({ label: "Sites", path: null });
      crumbs.push({ label: "Manuals", path: null });
      return crumbs;
    }

    // /managesites/drawing/:siteId
    const adminDrawingMatch = pathname.match(/^\/managesites\/drawing\/(\d+)$/);
    if (adminDrawingMatch) {
      crumbs.push({ label: "Clients", path: "/manageclients" });
      crumbs.push({ label: "Sites", path: null });
      crumbs.push({ label: "Drawings", path: null });
      return crumbs;
    }

    // /managesites/changeRequest/:siteId
    const adminChangeRequestMatch = pathname.match(/^\/managesites\/changeRequest\/(\d+)$/);
    if (adminChangeRequestMatch) {
      crumbs.push({ label: "Clients", path: "/manageclients" });
      crumbs.push({ label: "Sites", path: null });
      crumbs.push({ label: "Change Requests", path: null });
      return crumbs;
    }

    // /managesites/:clientId (must be checked after sub-routes)
    const adminSitesMatch = pathname.match(/^\/managesites\/(\d+)$/);
    if (adminSitesMatch) {
      crumbs.push({ label: "Clients", path: "/manageclients" });
      crumbs.push({ label: "Sites", path: null });
      return crumbs;
    }

    // /drawing-detail/:id/:siteId (admin context)
    const adminDrawingDetailMatch = pathname.match(/^\/drawing-detail\/(\d+)\/(\d+)$/);
    if (adminDrawingDetailMatch) {
      crumbs.push({ label: "Clients", path: "/manageclients" });
      crumbs.push({ label: "Sites", path: null });
      crumbs.push({ label: "Drawings", path: null });
      if (category) crumbs.push({ label: category, path: null });
      return crumbs;
    }
  }

  return crumbs;
};

export default useBreadcrumbs;
